import type { LocalizationBundle } from '../i18n/core'
import type { DailyGift } from './dailyGiftApi'
import { ShopItem, PaymentBarData } from '../types'
import { logger } from '../utils/logger'
import { authService, getCachedInitData, fetchMobileInitData, clearCachedInitData } from './auth'

export interface ShopPromotions {
    webBonusPercent: number
    firstPurchaseBonusPercent: number
    firstPurchaseEligible: boolean
    pendingFirstPurchase?: { cartId: string; slug: string } | null
    excludedTypes: string[]
    webBonusText?: string
    firstPurchaseTitle?: string
    firstPurchaseSubtitle?: string
}
export interface ShopData {
    localization?: LocalizationBundle
    promotions?: ShopPromotions
    returnToGameUrl?: string

    chips: ShopItem[]
    jokers: ShopItem[]
    hotDeals: ShopItem[]
    dailyGift?: DailyGift | null
    jokerGift?: DailyGift | null
    mobilePaymentBar?: PaymentBarData
}

type LegacyShopData = Omit<ShopData, 'mobilePaymentBar'> & {
    mobilePaymentBar?: PaymentBarData
    mobileBonusBar?: PaymentBarData
    paymentBar?: PaymentBarData
}

/**
 * Extract data from wrapped response: { r: true, data: {...} }
 * Also handles already-unwrapped data
 */
const extractData = (response: unknown): LegacyShopData => {
    if (!response || typeof response !== 'object') {
        // If it's not an object, return empty structure
        return {
            chips: [],
            jokers: [],
            hotDeals: []
        }
    }
    
    const responseObj = response as Record<string, unknown>
    
    // Check if it's already unwrapped (has chips/jokers/hotDeals directly)
    if ('chips' in responseObj || 'jokers' in responseObj || 'hotDeals' in responseObj) {
        return response as LegacyShopData
    }
    
    // Otherwise, try to extract from wrapper { r: boolean, data: {...} }
    if ('data' in responseObj && responseObj.data && typeof responseObj.data === 'object') {
        const wrappedData = responseObj.data as Record<string, unknown>
        // Check if the wrapped data has the shop structure
        if ('chips' in wrappedData || 'jokers' in wrappedData || 'hotDeals' in wrappedData) {
            return wrappedData as LegacyShopData
        }
    }
    
    // Fallback: return as-is (might already be the data)
    return response as LegacyShopData
}

const normalizeShopData = (data: LegacyShopData | unknown): ShopData => {
    // Extract data from potential wrapper (handles both wrapped and unwrapped)
    const extracted = extractData(data)
    
    const { mobilePaymentBar, mobileBonusBar, paymentBar, ...rest } = extracted

    return {
        ...rest,
        mobilePaymentBar: mobilePaymentBar ?? mobileBonusBar ?? paymentBar
    }
}

export const getShopData = async (options?: { refresh?: boolean }): Promise<ShopData> => {
    if (options?.refresh) clearCachedInitData()
    try {
        // Wait for login to complete if authentication is in progress
        await authService.waitForLogin()

        // Check for cached init data first (from either login or previous call)
        const cachedData = getCachedInitData()
        if (cachedData) {
            logger.log('API', 'Using cached mobileInitData', {
                hasCachedData: !!cachedData,
                cachedDataType: typeof cachedData,
                cachedDataKeys: cachedData && typeof cachedData === 'object' ? Object.keys(cachedData as Record<string, unknown>) : [],
            })
            const normalized = normalizeShopData(cachedData)
            logger.log('API', 'Normalized shop data from cache', {
                hasChips: !!normalized.chips,
                chipsCount: normalized.chips?.length || 0,
                hasJokers: !!normalized.jokers,
                jokersCount: normalized.jokers?.length || 0,
                hasHotDeals: !!normalized.hotDeals,
                hotDealsCount: normalized.hotDeals?.length || 0,
                hasPaymentBar: !!normalized.mobilePaymentBar,
                normalizedKeys: Object.keys(normalized),
            })
            return normalized
        }

        // If we have login data (from mobileInitData call), use it instead of calling again
        const loginData = authService.getLoginData()
        if (loginData && !options?.refresh) {
            logger.log('API', 'Using shop data from login response', {
                hasLoginData: !!loginData,
                loginDataType: typeof loginData,
                loginDataKeys: loginData && typeof loginData === 'object' ? Object.keys(loginData as Record<string, unknown>) : [],
                hasChips: loginData && typeof loginData === 'object' && 'chips' in (loginData as Record<string, unknown>),
                hasJokers: loginData && typeof loginData === 'object' && 'jokers' in (loginData as Record<string, unknown>),
                hasHotDeals: loginData && typeof loginData === 'object' && 'hotDeals' in (loginData as Record<string, unknown>),
            })
            
            const normalized = normalizeShopData(loginData as LegacyShopData)
            logger.log('API', 'Normalized shop data', {
                hasChips: !!normalized.chips,
                chipsCount: normalized.chips?.length || 0,
                hasJokers: !!normalized.jokers,
                jokersCount: normalized.jokers?.length || 0,
                hasHotDeals: !!normalized.hotDeals,
                hotDealsCount: normalized.hotDeals?.length || 0,
                hasPaymentBar: !!normalized.mobilePaymentBar,
            })
            return normalized
        }

        // Otherwise, fetch shop data using session cookie
        // Use shared fetch function which handles caching
        const fetchedData = await fetchMobileInitData({ method: 'GET' })
        
        logger.log('API', 'Fetched shop data from API (before normalization)', {
            fetchedDataType: typeof fetchedData,
            fetchedDataKeys: fetchedData && typeof fetchedData === 'object' ? Object.keys(fetchedData as Record<string, unknown>) : [],
            hasChips: fetchedData && typeof fetchedData === 'object' && 'chips' in (fetchedData as Record<string, unknown>),
            hasJokers: fetchedData && typeof fetchedData === 'object' && 'jokers' in (fetchedData as Record<string, unknown>),
            hasHotDeals: fetchedData && typeof fetchedData === 'object' && 'hotDeals' in (fetchedData as Record<string, unknown>),
        })
        
        const normalized = normalizeShopData(fetchedData)
        
        logger.log('API', 'Normalized shop data from API', {
            hasChips: !!normalized.chips,
            chipsCount: normalized.chips?.length || 0,
            hasJokers: !!normalized.jokers,
            jokersCount: normalized.jokers?.length || 0,
            hasHotDeals: !!normalized.hotDeals,
            hotDealsCount: normalized.hotDeals?.length || 0,
            hasPaymentBar: !!normalized.mobilePaymentBar,
        })
        
        return normalized
    } catch (error) {
        logger.warn('API', 'Failed to load shop data:', error)
        throw error
    }
}

